import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound, ShieldCheck } from "lucide-react";
import SEO from "../components/SEO";
import { supabase } from "../supabaseClient";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        setError("");
        setIsLoading(true);

        try {
            const { error: supabaseError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (supabaseError) {
                setError(supabaseError.message);
                setIsLoading(false);
                return;
            }

            setIsLoading(false);
            setIsSuccess(true);
        } catch (err) {
            setError("Something went wrong. Please try again later.");
            setIsLoading(false);
        }
    };

    return (
        <div className="flex justify-center items-center py-20 px-4">
            <SEO
                title="Reset Your Password"
                description="Forgot your blinqbooks password? Enter your email to receive a secure reset link."
            />

            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-[2rem] p-8 md:p-12 w-full max-w-lg transition-all duration-500 relative overflow-hidden ${isLoading ? 'scale-[1.02]' : ''}`}
            >
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-400/5 rounded-full blur-3xl pointer-events-none"></div>

                {/* Back to Login link */}
                <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm text-secondary dark:text-gray-400 hover:text-accent transition-colors mb-8 group"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    Back to Login
                </Link>

                {/* Header */}
                <div className="text-center mb-10 relative z-10">
                    <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                        <KeyRound className="w-8 h-8 text-accent" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black text-primary dark:text-white mb-3">
                        {isSuccess ? 'Check Your Email' : 'Reset Your Password'}
                    </h1>
                    <p className="text-secondary dark:text-gray-400 text-sm md:text-base max-w-sm mx-auto">
                        {isSuccess
                            ? "We've sent you a secure link to reset your password."
                            : "No worries! Enter the email address associated with your account and we'll send you a reset link."
                        }
                    </p>
                </div>

                {!isSuccess ? (
                    <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setError("");
                                    }}
                                    placeholder="name@company.com"
                                    required
                                    disabled={isLoading}
                                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-accent outline-none transition-all"
                                />
                            </div>
                            {error && (
                                <motion.p
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="text-red-500 text-xs mt-1 ml-1"
                                >
                                    {error}
                                </motion.p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || !email}
                            className={`w-full py-4 mt-4 font-bold text-white rounded-full transition-all duration-300 flex items-center justify-center gap-2 group ${isLoading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : !email
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-accent hover:bg-accent-hover transform hover:-translate-y-1 active:scale-95'
                                }`}
                        >
                            {isLoading ? (
                                <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <>Send Reset Link</>
                            )}
                        </button>

                        {/* Security note */}
                        <div className="flex items-start gap-3 mt-6 p-4 rounded-xl bg-slate-500/5 dark:bg-white/5">
                            <ShieldCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-secondary dark:text-gray-400 leading-relaxed">
                                For your security, the reset link will expire in 15 minutes. If you don't receive an email, check your spam folder.
                            </p>
                        </div>
                    </form>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="text-center py-6 relative z-10"
                    >
                        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Mail className="w-10 h-10" />
                        </div>
                        <p className="text-secondary dark:text-gray-300 mb-2">
                            We've sent a password reset link to
                        </p>
                        <p className="font-bold text-primary dark:text-white mb-8 text-lg">
                            {email}
                        </p>

                        <div className="space-y-4">
                            <button
                                onClick={() => {
                                    setIsSuccess(false);
                                    setEmail("");
                                }}
                                className="text-accent text-sm font-bold hover:underline"
                            >
                                Didn't receive it? Try again
                            </button>

                            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 text-sm text-secondary dark:text-gray-400 hover:text-accent transition-colors group"
                                >
                                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                    Return to Login
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        </div>
    );
}
